import styled, { css } from "styled-components";
import {
  NAME_ABOVE_PRODUCT_LAYOUT,
  NAME_AND_PRICE_SIDE_BY_SIDE_PRODUCT_LAYOUT,
  NAME_BENEATH_PRODUCT_LAYOUT,
  PRICE_IMAGE_SIDE_PRODUCT_LAYOUT,
  THREE_COLUMNS_PRODUCT_LAYOUT,
  TWO_COLUMNS_NAME_LEFT_PRODUCT_LAYOUT,
} from "../../WorkSpace/CanvasViewer/EncarteConstants";

export const Name = styled.span`
  ${({ fontSize, canvasScale, txtColor, nameMultiplier = 1 }) => css`
    font-size: ${fontSize * nameMultiplier * canvasScale}px;
    color: ${txtColor ?? "black"};
  `}
  bottom: 0;
  padding: 5% 10%;
  text-transform: uppercase;
  font-weight: 600;
  line-height: 1;
  align-self: center;
  text-align: center;
  display: block;
  opacity: 1.5;
  width: 100%;
`;

// Matches the Obs font size, line-height and vertical padding below.
export const getObsHeight = (fontSize, canvasScale) =>
  fontSize * canvasScale * (0.7 * 1.1 + 0.08 * 2);

const withObsRow = (rows, obsHeight) =>
  obsHeight ? `${rows} ${obsHeight}px` : rows;

export const Obs = styled.span`
  ${({ fontSize, canvasScale, txtColor, bgColor }) => css`
    font-size: ${fontSize * 0.7 * canvasScale}px;
    color: ${txtColor ?? "black"};
    background-color: ${bgColor ?? "transparent"};
    padding: ${fontSize * 0.08 * canvasScale}px
      ${fontSize * 0.3 * canvasScale}px;
  `}
  grid-column: 1 / -1;
  grid-row: -2 / -1;
  align-self: end;
  z-index: 2;
  white-space: nowrap;
  line-height: 1.1;
  font-weight: 600;
  pointer-events: none;
  width: 100%;
  text-align: center;
  text-transform: uppercase;
`;

export const ImageWrapper = styled.div`
  width: 100%;
  height: 100%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const Image = styled.div`
  position: relative;
  background-position: center;
  background-repeat: no-repeat;
  width: 100%;
  overflow: hidden;
  ${({ url, hover, height, backgroundSize, imageZoom }) => {
    const scale = imageZoom ? imageZoom / 100 : 1;
    return css`
      background-image: url("${url}");
      cursor: pointer;
      height: ${height ?? "100%"};
      background-size: ${backgroundSize ?? "contain"};
      transform: scale(${scale});
      ${hover &&
      css`
        &&:hover {
          background-color: rgba(1, 1, 1, 0.15);
        }
      `}
    `;
  }}
`;

export const ActionBox = styled.div`
  width: 80%;
  height: min-content;
  display: flex;
  flex-direction: column;
  justify-content: center;
  background-color: #f6f6f6;
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
  position: absolute;
  top: 0;
  transform: translateY(-100%);
  left: 1rem;
  align-items: center;
  font-size: 1.2rem;
  z-index: 10;
  padding: 0.5rem 0;
  gap: 3px;

  span {
    transition: all ease-in-out 200ms;
    width: 100%;
    text-align: left;
    z-index: 10;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 2px;
    padding: 6px;
  }

  span:hover {
    background-color: #e9e9e9;
    font-weight: bold;
    color: #d97706;
    border-radius: 5px;
  }

  &&:after {
    content: "";
    position: absolute;
    width: 0;
    height: 0;
    bottom: -10px;
    left: 0;
    border-left: 0px solid transparent;
    border-right: 25px solid transparent;
    border-top: 20px solid #f6f6f6;
  }
`;

const getLayoutStyle = (productLayout, canvasScale, obsHeight = 0) => {
  switch (productLayout) {
    case TWO_COLUMNS_NAME_LEFT_PRODUCT_LAYOUT:
      return css`
        position: relative;
        grid-template-rows: ${withObsRow("1fr 1fr", obsHeight)};
        ${Name} {
          grid-row: 1/2;
          grid-column: 1/2;
        }
        ${ImageWrapper} {
          position: unset;
          background-size: contain;
          grid-column: 2/3;
          grid-row: 1/3;
        }
      `;

    case NAME_BENEATH_PRODUCT_LAYOUT:
      return css`
        grid-template-rows: ${withObsRow("2fr 0.3fr 0.3fr", obsHeight)};
        ${Name} {
          grid-row: 2/3;
          grid-column: 1/3;
          align-self: start;
        }
        ${ImageWrapper} {
          grid-row: 1/2;
          grid-column: 1/3;
        }
        .price {
          grid-row: 3/4;
          grid-column: 1/3;
        }
        .product-name {
          border-bottom-right-radius: ${30 * canvasScale}px;
          border-bottom-left-radius: ${30 * canvasScale}px;
        }
      `;

    case NAME_AND_PRICE_SIDE_BY_SIDE_PRODUCT_LAYOUT:
      return css`
        grid-template-rows: ${withObsRow("2fr 0.3fr", obsHeight)};
        position: relative;
        ${Name} {
          grid-row: 2/3;
          grid-column: 1/2;
        }
        ${ImageWrapper} {
          grid-column: 1/3;
          grid-row: 1/2;
          min-width: 100%;
          min-height: 100%;
          position: absolute;
        }
        .price {
          grid-column: 2/3;
          grid-row: 2/3;
          min-width: 0;
        }
      `;

    case PRICE_IMAGE_SIDE_PRODUCT_LAYOUT:
      return css`
        grid-template-rows: ${withObsRow("0.2fr 1fr", obsHeight)};
        grid-template-columns: 1fr;
        align-content: end;
        ${Name} {
          grid-column: 1/3;
        }
        ${Image} {
          background-position: left;
        }
        ${ImageWrapper} {
          grid-row: 2/3;
        }
        .price {
          bottom: calc(10% + ${obsHeight}px);
          right: 0;
          position: absolute;
          height: min-content;
          width: min-content;
        }
      `;

    case NAME_ABOVE_PRODUCT_LAYOUT:
      return css`
        grid-template-rows: ${withObsRow("0.3fr 2fr", obsHeight)};
        grid-template-columns: 1fr;
        flex-direction: column-reverse;
        position: relative;
        ${Name} {
          z-index: 1;
          grid-row: 1/2;
        }
        ${ImageWrapper} {
          justify-self: center;
          align-self: center;
          padding-bottom: 10%;
        }
        .price {
          position: absolute;
          left: 0;
          height: min-content;
          bottom: ${obsHeight}px;
          width: 100%;
        }
      `;

    case THREE_COLUMNS_PRODUCT_LAYOUT:
      return css`
        grid-template-columns: 1fr 1fr 1fr;
        grid-template-rows: ${withObsRow("1fr", obsHeight)};
      `;

    default:
      return null;
  }
};

export const Container = styled.div`
  justify-content: center;
  width: 100%;
  height: 100%;
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr 1fr;
  user-select: none;
  overflow: hidden;
  position: relative;
  cursor: pointer;

  ${({ productLayout, canvasScale, obsHeight }) =>
    getLayoutStyle(productLayout, canvasScale, obsHeight)}

  ${({ bgColor, canvasScale, productsLength }) => {
    const radius = `${(80 / productsLength) * canvasScale}px`;
    return css`
      background-color: ${bgColor !== "" ? bgColor : "transparent"};

      ${productsLength === 1
        ? css`
            border-top-right-radius: ${radius} !important;
            border-top-left-radius: ${radius} !important;
          `
        : css`
            border-radius: ${radius} !important;
          `}
    `;
  }}

  ${({ row, column, font }) => css`
    grid-column: ${column};
    grid-row: ${row};
    font-family: ${font};
  `}

  .name-price {
    align-items: center;
    gap: 2.6rem;
  }

  ${({ selected, isProductClickable }) =>
    selected &&
    isProductClickable &&
    css`
      border: 2px solid "black";
      position: relative;
      overflow: visible;
    `}
`;
